import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  FileSignature, Handshake, FolderOpen, Receipt, CalendarClock, Users, BookOpen,
  Store, FileSearch, ChefHat, CheckSquare, TrendingUp, Shield, Database, Eye,
  ArrowRight, Sparkles, MessageSquareHeart, GraduationCap, HelpCircle, Rocket,
  Mail, Heart, MessageSquare, Search,
} from 'lucide-react';
import DivisionWishlistForm from '@/components/DivisionWishlistForm';

/**
 * Role-aware Quick-Start Guide
 * Shows each user only the flows that apply to their role/division.
 * Each card is a self-contained mini-playbook: icon, title, 2-3 sentence description,
 * step-by-step bullets, and a single "Go there now" CTA that deep-links into the app.
 */

// ---------- step-cards shared vocabulary ----------
const Step = ({ n, children }) => (
  <div className="flex gap-2 text-[12px]" style={{ color: '#374151' }}>
    <span className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#E0F2F1', color: '#2A7B88' }}>{n}</span>
    <span>{children}</span>
  </div>
);

function GuideCard({ icon: Icon, title, summary, steps, ctaLabel, onCta, accent = '#2A7B88' }) {
  return (
    <div className="bg-white rounded-xl border p-5 flex flex-col h-full" style={{ borderColor: '#E5E1DC' }} data-testid={`guide-card-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon size={18} style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold leading-snug" style={{ color: '#1B2A4A' }}>{title}</h3>
          <p className="text-[12px] mt-0.5 leading-relaxed" style={{ color: '#6B7280' }}>{summary}</p>
        </div>
      </div>
      <div className="space-y-1.5 my-3">
        {steps.map((s, i) => <Step key={i} n={i + 1}>{s}</Step>)}
      </div>
      {onCta && (
        <button onClick={onCta}
          className="mt-auto self-start inline-flex items-center gap-1 text-[12px] font-semibold hover:gap-2 transition-all"
          style={{ color: accent }}
          data-testid={`guide-cta-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
          {ctaLabel} <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}

// ---------- content builders ----------
function b2bLeaderCards(navigate, divId) {
  return [
    {
      icon: Handshake, title: '1. I signed a new contract',
      summary: 'Log a newly-signed deal so it shows up as Contracted revenue on your dashboard.',
      steps: [
        'Open the Pipeline tab on your division page.',
        'Find the deal card → move to "Closed Won" stage.',
        'Fill in: Client name, deal value, start & end dates.',
        'The contracted amount auto-appears on the dashboard KPI.',
      ],
      ctaLabel: 'Open Pipeline',
      onCta: () => navigate(`/division/${divId}?tab=pipeline`),
    },
    {
      icon: FileSignature, title: '2. Set payment terms on the contract',
      summary: 'Configure how this contract bills: one-time, monthly retainer, or milestone.',
      steps: [
        'Open Contracts tab.',
        'Click the contract → "Edit Payment Structure".',
        'Choose: Monthly retainer, one-time, or milestones.',
        'Set Net days, advance %, schedule auto-generates.',
      ],
      ctaLabel: 'Open Contracts',
      onCta: () => navigate(`/division/${divId}?tab=contracts`),
      accent: '#BA7517',
    },
    {
      icon: FolderOpen, title: '3. Store contract PDF & client docs',
      summary: 'Every client has a dedicated Dossier for signed contracts, NDAs, KYC, meeting notes.',
      steps: [
        'Open Clients tab on your division page.',
        'Click the client name → Client Dossier opens.',
        'Go to "Documents" tab → drag & drop files.',
        'Also capture: Contacts, Meeting Notes, Payment History.',
      ],
      ctaLabel: 'Open Clients',
      onCta: () => navigate(`/division/${divId}?tab=clients`),
      accent: '#3B5BA9',
    },
    {
      icon: Receipt, title: '4. Record revenue (when invoice is raised)',
      summary: 'Log actual invoices — the ONLY way realised revenue should move on the dashboard.',
      steps: [
        'Open Monthly Billing tab (or Pipeline → Record Revenue).',
        'Pick the contract + month.',
        'Enter: Amount, INVOICE # (required), PO #, recognised date.',
        'No invoice yet? Use "Projected", not "Realised".',
      ],
      ctaLabel: 'Open Monthly Billing',
      onCta: () => navigate(`/division/${divId}?tab=monthly-billing`),
      accent: '#2D8B5E',
    },
    {
      icon: CalendarClock, title: '5. Track receivables & payment aging',
      summary: 'See what\'s overdue, what\'s invoiced-not-received, and what\'s upcoming this week.',
      steps: [
        'Dashboard → "Active Receivables" panel (auto-shows overdue).',
        'Copy the WhatsApp reminder with one click.',
        'Mark as received when money hits the account.',
      ],
      ctaLabel: 'View receivables',
      onCta: () => navigate(`/dashboard#receivables`),
      accent: '#D4726A',
    },
  ];
}

function fbLeaderCards(navigate, divId) {
  return [
    {
      icon: Store, title: '1. Enter daily sales',
      summary: 'Record yesterday\'s revenue per outlet — drives the entire dashboard.',
      steps: [
        'Open any outlet page → Daily Sales tab.',
        'Enter: date, amount, channel (Dine-in/Swiggy/Zomato/Takeaway).',
        'Import historical CSV if catching up.',
      ],
      ctaLabel: 'Open Daily Sales',
      onCta: () => navigate(`/division/${divId}?tab=daily-sales`),
    },
    {
      icon: ChefHat, title: '2. Keep food cost under 15%',
      summary: 'Track recipe-level food cost vs selling price to catch menu drift early.',
      steps: [
        'TDB → Cost Module → Recipes tab.',
        'Add BOM (ingredients × quantity × price).',
        'Target = 15% food cost; system flags if over.',
      ],
      ctaLabel: 'Open Cost Module',
      onCta: () => navigate(`/tdb/cost`),
      accent: '#BA7517',
    },
    {
      icon: FileSearch, title: '3. Scan vendor invoices with AI',
      summary: 'Upload a photo of a supplier bill — Gemini Vision extracts line items into JSON.',
      steps: [
        'TDB → Cost Module → Invoice Scanner.',
        'Upload photo or PDF.',
        'Review extracted data → click Save.',
        'Costs auto-allocate to monthly P&L.',
      ],
      ctaLabel: 'Scan invoice',
      onCta: () => navigate(`/tdb/cost?tab=invoices`),
      accent: '#3B5BA9',
    },
  ];
}

function trainingCards(navigate, divId, role) {
  return [
    {
      icon: GraduationCap, title: 'Training Library (SOPs)',
      summary: 'Add, edit, assign and track every SOP — the written standard behind every shift.',
      steps: [
        'Open an outlet page → SOPs tab.',
        '"+ Add SOP" to write a new standard. Upload video/audio/PDF alongside.',
        'Assign to roles or specific team members.',
        'Track acknowledgement % per outlet.',
      ],
      ctaLabel: role === 'ceo' ? 'Training Launch Summary' : 'Open Training',
      onCta: () => navigate(role === 'ceo' ? '/training/launch-summary' : `/division/${divId}?tab=training`),
      accent: '#1B2A4A',
    },
  ];
}

function commonCards(navigate, role) {
  return [
    {
      icon: MessageSquareHeart, title: 'Submit weekly update',
      summary: 'Monday ritual — 5-minute form with your KPIs, wins, risks, one reflection.',
      steps: [
        'Click "Submit Update" on your division page.',
        'Fill quantitative fields + one line on "the story behind the numbers".',
        'Submit before Mon 6pm; CEO reviews on Tue morning.',
      ],
      ctaLabel: 'Submit weekly update',
      onCta: () => navigate('/update'),
      accent: '#2A7B88',
    },
    {
      icon: Users, title: 'People directory',
      summary: 'Your team, their roles, training progress, birthdays, documents.',
      steps: [
        'Division page → People tab.',
        'Click a name → full profile (KYC, emergency contact, uniforms, training %).',
        'Add new joiner → auto-assigned Day-1 SOPs.',
      ],
      ctaLabel: 'Open People',
      onCta: () => navigate(role === 'ceo' ? '/dashboard' : '/update'),
      accent: '#BA7517',
    },
    {
      icon: BookOpen, title: 'Our Story, Values & Mission',
      summary: 'Why we exist. Who we are. What we promise guests and each other.',
      steps: [
        'Click "Our Story" in the top nav anytime.',
        'Read the founding story, sister brands, service philosophy.',
        'Share in morning briefings — every employee is a storyteller.',
      ],
      ctaLabel: 'Read Our Story',
      onCta: () => navigate('/our-story'),
      accent: '#3B5BA9',
    },
  ];
}

function concierge_b2c_cards(navigate) {
  return [
    {
      icon: Mail, title: 'Newsletter — author, approve, send',
      summary: 'Monthly Concierge® newsletter — Claude auto-drafts, you edit, Tanisha bulk-sends via Zoho Campaigns.',
      steps: [
        'Client Intelligence → JLR (or any client) → Newsletters tab.',
        'Duplicate previous month or click "Auto-draft with Claude".',
        'Click "Auto-fill images" to populate venue photos in 30-60s.',
        'Send → Team Preview → Approve → Send → Bulk Handover (1 email to Tanisha).',
      ],
      ctaLabel: 'Open Newsletters',
      onCta: () => navigate('/client-intelligence/jlr'),
      accent: '#7A5C2B',
    },
    {
      icon: Sparkles, title: 'Soul Sheets — member intelligence',
      summary: 'AI-written narrative profiles + Concierge Notes for every member, built from Soul Form + Zoho service history.',
      steps: [
        'Client Intelligence → JLR → Soul tab.',
        'Top: "Soul Triggers" — daily action list (form completions, anniversaries, etc.).',
        'Click any member → drawer with profile + Concierge Notes + service history.',
        '"Generate" rewrites with latest data. "Push to Zoho" saves as a CRM note.',
      ],
      ctaLabel: 'Open Soul tab',
      onCta: () => navigate('/client-intelligence/jlr'),
      accent: '#7A5C2B',
    },
    {
      icon: Heart, title: 'Member Care Workbench — daily review',
      summary: 'Per Dipali doctrine: AI generates → human governs → Zoho executes. MCE drafts at 03:00 IST → you review at 09:00 → push approved drafts to Zoho.',
      steps: [
        'Top nav → Workbench. Drafts grouped by Birthdays / Dormant / Soul / Curated.',
        'Filter by client (JLR / AaBee), LSM, or trigger type.',
        'Click any draft → Edit inline (Save) or click "Reset to original" if your edit was worse.',
        'Click ✨ Suggest on dormant rows → Claude reads last 5 tickets + Soul Form for ONE specific anchor.',
        'See an amber "N drafts have an empty body" banner? Click "Fix now · regenerate" — Claude re-runs in bulk.',
        'Click ✨ Regenerate with Claude inside any drawer for a fresh take on a single draft.',
        'Select drafts → Push to Zoho in bulk (with confirmation modal). Or skip with reason.',
        'Click [CSV] to export current view for offline review in Excel.',
      ],
      ctaLabel: 'Open Workbench',
      onCta: () => navigate('/member-care/workbench'),
      accent: '#A32D2D',
    },
    {
      icon: Sparkles, title: 'Generate JLR / AaBee deck (PPT)',
      summary: 'Click Generate Report on the CI dashboard → Preview every Claude-written narrative → edit any line → ship.',
      steps: [
        'Client Intelligence → JLR → click "Generate Report" header button.',
        'Pick Apr 2026 preset (or any month / custom range).',
        'Click "Preview & generate" — Claude drafts 27-29 narratives in 60s.',
        'Read each section. Click "Rewrite with instruction" to ask Claude for a shorter / Mumbai-specific / less-lyrical version.',
        'When happy → Generate Final PPT → downloads in 2 seconds.',
      ],
      ctaLabel: 'Open CI · JLR',
      onCta: () => navigate('/client-intelligence/jlr'),
      accent: '#7A5C2B',
    },
    {
      icon: MessageSquare, title: 'Operator Mira — chat & WhatsApp',
      summary: 'In-house Operator-Brain. Ask anything, or let Mira take action (update Soul, push CRM, draft tickets).',
      steps: [
        '"Mira" tab in top nav (or message the Concierge® WA number).',
        'Ask: "Soul of {name}", "How is JLR Goa doing?", "Compare JLR vs AaBee last week".',
        'Switch to Operator mode for write actions (CEO only).',
        'Every WA call audit-logged in mira_wa_log.',
      ],
      ctaLabel: 'Open Mira',
      onCta: () => navigate('/mira'),
      accent: '#1B2A4A',
    },
  ];
}


function ceoCards(navigate) {
  return [
    {
      icon: Eye, title: 'CEO cockpit — single pane of glass',
      summary: 'Group-level revenue, red alerts, opportunities, and action queue — all on one dashboard.',
      steps: [
        'Dashboard = your home screen.',
        'Active Receivables → who hasn\'t paid + one-click WhatsApp reminders.',
        '5-year Revenue Journey → group story at a glance.',
      ],
      ctaLabel: 'Go to dashboard',
      onCta: () => navigate('/dashboard'),
    },
    {
      icon: Shield, title: 'Admin — users, permissions, targets',
      summary: 'Add/remove users, set roles, adjust division + outlet targets, tune alert thresholds.',
      steps: [
        'Admin → Users (add/remove, reset password).',
        'Admin → Permissions (per-role matrix — override if needed).',
        'Admin → Targets (annual & quarterly per division/outlet).',
      ],
      ctaLabel: 'Open Admin',
      onCta: () => navigate('/admin'),
      accent: '#1B2A4A',
    },
    {
      icon: Database, title: 'Backup & Restore (your safety net)',
      summary: 'Daily auto-snapshots, on-demand download, one-click restore from any Gold Master.',
      steps: [
        'Admin → Backup tab.',
        'Download Full Snapshot any time (JSON).',
        'Pin a Gold Master before risky changes — never rotated out.',
      ],
      ctaLabel: 'Open Backup',
      onCta: () => navigate('/admin?tab=backup'),
      accent: '#D4726A',
    },
    {
      icon: TrendingUp, title: 'IP Register & Mission',
      summary: 'Track trademarks, patents, missions per entity — the intangible ledger.',
      steps: [
        'Top nav → IP Register (trademarks, registrations, renewals).',
        'Top nav → Values (audits + commitments).',
      ],
      ctaLabel: 'Open IP Register',
      onCta: () => navigate('/ip-register'),
      accent: '#BA7517',
    },
  ];
}

function accountsCards(navigate) {
  return [
    {
      icon: CalendarClock, title: 'Cross-division receivables',
      summary: 'You have cross-division access — see every invoice across every entity in one view.',
      steps: [
        'Dashboard → Active Receivables panel.',
        'Use the division filter to drill in.',
        'Edit payment terms on any contract regardless of division.',
      ],
      ctaLabel: 'Open dashboard',
      onCta: () => navigate('/dashboard'),
    },
    {
      icon: FileSignature, title: 'Payment Terms Engine',
      summary: 'Configure how each contract bills (retainer, milestone, net-X) — schedules auto-generate.',
      steps: [
        'Any division → Contracts tab → Edit.',
        'Set payment structure — schedule rebuilds.',
        '"Terms Pending" widget on CEO dashboard flags anything missing.',
      ],
      ctaLabel: 'Open a division',
      onCta: () => navigate('/division/cc-ops?tab=contracts'),
      accent: '#3B5BA9',
    },
  ];
}

// ---------- main component ----------
export default function QuickStartGuide() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCEO = user?.role === 'ceo';
  const isAccounts = user?.role === 'submitter' && (user?.email === 'nihal@mindescapes.in' || user?.email === 'lohita@clubconcierge.in');
  const divId = user?.division_id || (user?.additional_division_ids?.[0]) || 'me';
  const isFB = ['me', 'tdb'].includes(divId);

  const sections = useMemo(() => {
    const out = [];
    if (isCEO) {
      out.push({ title: 'Your CEO cockpit', cards: ceoCards(navigate) });
      out.push({ title: 'Concierge® B2C — Members, Newsletters, Mira', cards: concierge_b2c_cards(navigate) });
    } else if (isAccounts) {
      out.push({ title: 'Accounts workflow', cards: accountsCards(navigate) });
    } else if (isFB) {
      out.push({ title: 'Operating your outlets', cards: fbLeaderCards(navigate, divId) });
    } else {
      out.push({ title: 'Your B2B revenue workflow', cards: b2bLeaderCards(navigate, divId) });
      // Concierge leaders (Nisha, Tanisha, Shiva, Nikita, Samar) also see the B2C playbook.
      if (['cc-clubs', 'cc-ops', 'lc', 'cs'].includes(divId)) {
        out.push({ title: 'Concierge® B2C — Members, Newsletters, Mira', cards: concierge_b2c_cards(navigate) });
      }
    }
    out.push({ title: 'Training — the written standard', cards: trainingCards(navigate, divId, user?.role) });
    out.push({ title: 'Every day essentials', cards: commonCards(navigate, user?.role) });
    return out;
  }, [isCEO, isAccounts, isFB, divId, user?.role, navigate]);

  return (
    <div className="max-w-6xl mx-auto pb-10" data-testid="quick-start-guide">
      {/* Hero */}
      <div className="rounded-2xl p-6 mb-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #2A7B88 100%)' }}>
        <div className="flex items-start gap-3 relative z-10">
          <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <Rocket size={22} style={{ color: '#fff' }} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#fff' }}>
              Welcome to ScaleBoard, {user?.name?.split(' ')[0] || 'team'}.
            </h1>
            <p className="text-sm mt-1 max-w-2xl" style={{ color: '#E5E1DC' }}>
              Your role-specific playbook — where every number comes from, where to put things, and the shortest path to every action.
              Bookmark this page and come back whenever you're unsure.
            </p>
          </div>
        </div>
        <Sparkles size={120} className="absolute -right-6 -bottom-6 opacity-10" style={{ color: '#fff' }} />
      </div>

      {/* Sections */}
      {sections.map((sec) => (
        <section key={sec.title} className="mb-7">
          <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#9CA3AF' }}>{sec.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sec.cards.map((card, i) => (
              <GuideCard key={i} {...card} />
            ))}
          </div>
        </section>
      ))}

      {/* Division wishlist form — for everyone except CEO (CEO sees them in Admin) */}
      {!isCEO && <DivisionWishlistForm />}

      {/* Footer tip */}
      <div className="mt-8 rounded-lg border p-4 flex items-start gap-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
        <CheckSquare size={16} style={{ color: '#2D8B5E' }} className="mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Rule of thumb</p>
          <p className="text-[12px] mt-0.5" style={{ color: '#6B7280' }}>
            Numbers on the dashboard only move when <b>you explicitly click a button tied to a real document</b>
            &nbsp;(invoice, PO, receipt). If it ever looks wrong, check the Realised Revenue Ledger on your division page — every entry is editable or deletable with one click.
          </p>
        </div>
      </div>
    </div>
  );
}
